import { RAISE_DEG, YAW_DEG, renderAxisViews } from './meshAxisViews';
import { mmScale } from './meshAnnotations';
import { builtBoxOf } from './openaiBlenderApi';
import type { PartSize } from './partsRun';
import { isTrueScale, jobExtents, meshSrc, meshStamp, type Job3D } from './threeDApi';

// ONE PART'S 3D MODEL, PHOTOGRAPHED FOR A MODEL TO READ: the views asked for, each inside the
// part's measured box — twelve edges coloured by axis, three of them carrying their length in
// millimetres.
//
// Shared by the two calls that show a part's mesh: Phase 7's movement (front and top, to say
// which way a part lies) and Find holes (all six, to say where on it a bore goes).
//
// In millimetres through the same `mmScale` every size in the app goes through: the mesh is
// loaded raw in its own units, so a screw modelled in metres would otherwise be labelled
// "depth: 0 mm". No box where the scale is unknown — pictures, and no numbers on them.
const BOX_WORDS = { width: 'width', depth: 'depth', height: 'height' };

export async function renderPartViews(
  job: Job3D,
  size: PartSize | null | undefined,
  only?: readonly string[],
  // Find holes' pictures: the top and bottom front-up (see renderAxisViews), and no lengths
  // written on the box — its text carries every face's size.
  forHoles = false,
): Promise<{ name: string; dataUrl: string }[]> {
  const raw = await jobExtents(job.id);
  const k = raw ? mmScale(raw, size ?? null, isTrueScale(job), builtBoxOf(job)) : null;
  const made = await renderAxisViews(
    meshSrc(job.id, meshStamp(job)),
    512,
    only,
    k ? { mmPerUnit: k, words: BOX_WORDS, labels: !forHoles } : undefined,
    forHoles,
  );
  return made.views;
}

// AND HOW A PROMPT SAYS WHAT THOSE PICTURES ARE, in one place for every call that sends them: which
// side each of the two is of, that the camera is turned off that side (see YAW_DEG), and what the
// coloured box round the part is. Unsaid, the tilt reads as a part lying askew and the box as a
// decoration.
export const FRONT_VIEW_WORDS = 'seen from the FRONT (camera on −y, looking along +y, z up)';
export const TOP_VIEW_WORDS = 'seen from ABOVE (camera on +z, looking down, +y up)';
export const VIEW_TILT_WORDS = `The camera is turned ${YAW_DEG}° sideways and raised ${RAISE_DEG}° off the axis `
  + 'named, so the part is seen slightly from one side rather than dead on.';
export const VIEW_BOX_WORDS = 'Each part is drawn inside its bounding box: edges along x are red, along y are '
  + 'green, along z are blue, and three of them are labelled with that axis\'s length in '
  + 'millimetres. So the axis a part is LONG along is the axis whose label carries its '
  + 'largest number, and the colour of an edge tells you which axis it is without '
  + 'measuring the picture.';
